/**
 * FteDemandStrip.tsx
 * The Phase-3 "rough demand" advisory strip (round-14a mockup, 2026-07-26),
 * HYBRID since Wave B (2026-07-29, qa/7-28-2026/DEV-PLAN-gigi-answers.md):
 *
 *   demand = baked person-hours (tests whose TaskType has a
 *            TaskTypeEffortProfile — Shawn's rock minutes, migration 119)
 *          + (remaining quote value ÷ hourly rate)     [the old heuristic]
 *
 * Baked lines' COST is excluded from the ÷rate half; people-effort lines
 * (Lab PM) are excluded from both. Served by GET /api/schedule/fte-demand;
 * falls back to the original cost-only math if that endpoint fails (e.g. a
 * stale backend), so the strip never goes blank on a version skew.
 *
 * A+C visibility rework (2026-07-30, qa/fte-demand-mockups-2026-07-29; the
 * B mini-bar shipped in v1.1.72 read too crowded — swapped for mockup A):
 *   A — colored figures ("13.5 h measured (rock) + $2,025 index/soil ÷ $200/h")
 *       plus small measured/estimated count chips;
 *   C — a "Breakdown" pill button expands the full arithmetic table
 *       (FteDemandBreakdown) under the strip. Collapsed by default.
 *
 * States: amber when planned falls short, quiet green when covered, and
 * NOTHING when the scenario has no priced quote (work-order / manual sources
 * get no invented numbers). Advisory only — never red, never blocking.
 */

import React, { useEffect, useState } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { alpha, useTheme } from '@mui/material/styles';

import { FteDemandBreakdown } from './FteDemandBreakdown';

import { getQuote } from '../../../../services/quoteService';
import {
  fetchFteConfig,
  fetchHybridFteDemand,
  type HybridFteDemand,
} from '../../../../services/scheduleFteConfig';
import { computeFteDemand } from '../../../../utils/fteDemand';

export interface FteDemandStripProps {
  source?: 'Quote' | 'WorkOrder' | null;
  sourceKey?: string | null;
  /** Σ of the per-week Team-need totals ÷ 100, over the project window. */
  plannedFteWeeks: number;
}

const fmtMoney = (n: number): string => `$${Math.round(n).toLocaleString('en-CA')}`;

/** Legacy fallback: whole quote value ÷ rate (pre-Wave-B behavior). */
async function fetchLegacyDemand(quoteKey: string): Promise<HybridFteDemand | null> {
  const [quote, cfg] = await Promise.all([getQuote(quoteKey), fetchFteConfig()]);
  const d = computeFteDemand(quote.Subtotal, quote.FxRateAtQuote, cfg.hourlyRate, cfg.hoursPerWeek);
  if (!d) return null;
  return {
    sourceType: 'Quote',
    sourceKey: quoteKey,
    bakedPersonHours: 0,
    bakedLines: [],
    residualValueCad: d.quoteValueCad,
    residualPersonHours: d.personHours,
    excludedEffortValueCad: 0,
    demandPersonHours: d.personHours,
    demandFteWeeks: d.demandFteWeeks,
    hourlyRate: cfg.hourlyRate,
    hoursPerWeek: cfg.hoursPerWeek,
  };
}

export const FteDemandStrip: React.FC<FteDemandStripProps> = ({ source, sourceKey, plannedFteWeeks }) => {
  const theme = useTheme();
  const [demand, setDemand] = useState<HybridFteDemand | null>(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    // Quote-sourced scenarios only; anything missing → the strip stays hidden.
    if (source !== 'Quote' || !sourceKey) { setDemand(null); return; }
    let cancelled = false;
    fetchHybridFteDemand(sourceKey)
      .catch(() => fetchLegacyDemand(sourceKey))
      .then((d) => {
        if (cancelled) return;
        // Nothing meaningful to show (empty/zero-value quote with no baked lines).
        setDemand(d && (d.demandPersonHours > 0 || d.bakedLines.length > 0) ? d : null);
      })
      .catch(() => { if (!cancelled) setDemand(null); });
    return () => { cancelled = true; };
  }, [source, sourceKey]);

  if (!demand) return null;
  const short = Math.round((demand.demandFteWeeks - plannedFteWeeks) * 10) / 10;
  const covered = short <= 0;
  const hasBaked = demand.bakedPersonHours > 0;

  // Gauge: planned fill (slate) against a demand tick (dark sage), scaled so
  // the larger of the two sits at ~90% of the track.
  const scale = Math.max(demand.demandFteWeeks, plannedFteWeeks, 0.1) / 0.9;
  const fillPct = Math.min(100, (plannedFteWeeks / scale) * 100);
  const markPct = Math.min(100, (demand.demandFteWeeks / scale) * 100);

  // A — colored figures + count chips (2026-07-30 feedback: the composition
  // mini-bar crowded the strip; mockup A's copy treatment reads lighter).
  const measuredColor = theme.schedule.committed;
  const estimatedColor = theme.schedule.machineTime;
  const measuredCount = demand.bakedLines.length;
  const estimatedCount = (demand.residualLines ?? []).length;

  const tooltip = hasBaked
    ? `Hybrid estimate: ${demand.bakedPersonHours} h from the lab's measured per-test minutes + ${fmtMoney(demand.residualValueCad)} of unprofiled work ÷ $${demand.hourlyRate}/h ≈ ${demand.residualPersonHours} h. Total ≈ ${demand.demandPersonHours} person-hours ≈ ${demand.demandFteWeeks} FTE-weeks (1 FTE = ${demand.hoursPerWeek} h/week). Click "Breakdown" for the line-by-line arithmetic.`
    : `Rough people-time from the quote value: ${fmtMoney(demand.residualValueCad)} at the junior rate ($${demand.hourlyRate}/h, admin-tunable) ≈ ${demand.residualPersonHours} person-hours ≈ ${demand.demandFteWeeks} FTE-weeks. Machine-heavy tests overstate this — treat it as a floor-check, not a target.`;

  const tint = covered ? theme.schedule.heatmapLow : theme.schedule.heatmapMid;
  return (
    <Box>
      <Tooltip title={tooltip} arrow placement="top">
        <Box
          sx={{
            display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap',
            mx: 1.5, mt: 1.25, px: 1.75, py: 0.9, borderRadius: '8px', fontSize: '0.8rem',
            backgroundColor: alpha(tint, covered ? 0.12 : 0.14),
            border: `1px solid ${alpha(tint, 0.45)}`,
          }}
        >
          <Typography component="span" sx={{ fontSize: 'inherit' }}>
            Rough demand <b style={{ fontVariantNumeric: 'tabular-nums' }}>≈{demand.demandFteWeeks} FTE-weeks</b>
          </Typography>

          {hasBaked ? (
            /* A — colored figures + count chips (mockup A, 2026-07-30). */
            <>
              <Typography component="span" sx={{ fontSize: '0.72rem', color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                <b style={{ color: measuredColor }}>{demand.bakedPersonHours} h</b> measured (rock)
                {' + '}
                <b style={{ color: estimatedColor }}>{fmtMoney(demand.residualValueCad)}</b> index/soil ÷ ${demand.hourlyRate}/h
              </Typography>
              <Typography
                component="span"
                sx={{
                  display: 'inline-flex', alignItems: 'center', gap: 0.5,
                  fontSize: '0.64rem', fontWeight: 700, color: measuredColor, whiteSpace: 'nowrap',
                  backgroundColor: alpha(measuredColor, 0.1), border: `1px solid ${alpha(measuredColor, 0.35)}`,
                  borderRadius: '999px', px: 0.9, py: 0.15,
                }}
              >
                <Box component="span" sx={{ width: 7, height: 7, borderRadius: '2px', backgroundColor: measuredColor }} />
                {measuredCount} test{measuredCount === 1 ? '' : 's'} measured
              </Typography>
              {estimatedCount > 0 && (
                <Typography
                  component="span"
                  sx={{
                    display: 'inline-flex', alignItems: 'center', gap: 0.5,
                    fontSize: '0.64rem', fontWeight: 700, color: estimatedColor, whiteSpace: 'nowrap',
                    backgroundColor: alpha(estimatedColor, 0.1), border: `1px solid ${alpha(estimatedColor, 0.35)}`,
                    borderRadius: '999px', px: 0.9, py: 0.15,
                  }}
                >
                  <Box component="span" sx={{ width: 7, height: 7, borderRadius: '2px', backgroundColor: estimatedColor }} />
                  {estimatedCount} estimated
                </Typography>
              )}
            </>
          ) : (
            <Typography component="span" sx={{ fontSize: '0.68rem', color: 'text.secondary' }}>
              {fmtMoney(demand.residualValueCad)} ÷ ${demand.hourlyRate}/h ÷ {demand.hoursPerWeek} h
            </Typography>
          )}

          <Box sx={{ flex: '1 1 140px', minWidth: 120, height: 8, borderRadius: '4px', backgroundColor: alpha(theme.schedule.noActivity, 0.18), position: 'relative', overflow: 'visible' }}>
            <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: `${fillPct}%`, borderRadius: '4px', backgroundColor: theme.schedule.machineTime }} />
            <Box sx={{ position: 'absolute', top: -2, bottom: -2, left: `${markPct}%`, width: '2px', backgroundColor: theme.schedule.committed }} />
          </Box>
          <Typography component="span" sx={{ fontSize: 'inherit' }}>
            planned <b style={{ fontVariantNumeric: 'tabular-nums' }}>{plannedFteWeeks}</b>
          </Typography>
          <Typography component="span" sx={{ fontSize: 'inherit', fontWeight: 800, color: covered ? theme.schedule.committed : theme.schedule.maintenance ?? tint }}>
            {covered ? '✓ covered' : `▲ ${short} short`}
          </Typography>

          {/* C — expand the full arithmetic under the strip. Styled as a real
              button (2026-07-30 feedback: the bare text link was easy to miss). */}
          <Typography
            component="button"
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.4,
              fontSize: '0.7rem', fontWeight: 700, fontFamily: 'inherit',
              color: open ? theme.palette.getContrastText(measuredColor) : measuredColor,
              backgroundColor: open ? measuredColor : alpha(measuredColor, 0.1),
              border: `1px solid ${alpha(measuredColor, 0.55)}`,
              borderRadius: '999px', px: 1.1, py: 0.35, cursor: 'pointer',
              '&:hover': { backgroundColor: open ? measuredColor : alpha(measuredColor, 0.2) },
            }}
          >
            Breakdown
            <ExpandMoreIcon sx={{ fontSize: 15, transition: 'transform 150ms', transform: open ? 'rotate(180deg)' : 'none' }} />
          </Typography>
        </Box>
      </Tooltip>

      {open && <FteDemandBreakdown demand={demand} />}
    </Box>
  );
};
